import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { Dialog } from '../../components/Dialog.js';
import { listRecipes } from '../../api/recipes.js';
import type { Recipe } from '../../api/types.js';
import type { MealSlot } from '../../api/calendarTypes.js';
import { MEAL_ICON, MEAL_LABEL } from '../../api/calendarTypes.js';

export interface AddMealDialogProps {
  open: boolean;
  onClose: () => void;
  slot: MealSlot;
  onPick: (recipeId: number) => Promise<void> | void;
}

export function AddMealDialog({
  open,
  onClose,
  slot,
  onPick,
}: AddMealDialogProps): ReactElement {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pickingId, setPickingId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const list = await listRecipes({ meal_type: slot });
        if (!cancelled) setRecipes(list);
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Failed to load recipes';
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, slot]);

  async function pick(id: number): Promise<void> {
    setPickingId(id);
    try {
      await onPick(id);
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add meal';
      setError(msg);
    } finally {
      setPickingId(null);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Add ${MEAL_LABEL[slot]} ${MEAL_ICON[slot]}`}
    >
      <div className="flex flex-col gap-2" data-testid="add-meal-dialog">
        {loading ? (
          <div
            className="text-sm"
            style={{ color: 'var(--text-muted)' }}
            data-testid="add-meal-loading"
          >
            Loading recipes…
          </div>
        ) : null}
        {error ? (
          <div
            className="rounded border p-2 text-sm"
            style={{
              borderColor: 'var(--border)',
              color: 'var(--text-muted)',
              background: 'color-mix(in srgb, #dc2626 8%, transparent)',
            }}
            data-testid="add-meal-error"
            role="alert"
          >
            {error}
          </div>
        ) : null}
        {!loading && recipes.length === 0 && !error ? (
          <div
            className="text-sm"
            style={{ color: 'var(--text-muted)' }}
            data-testid="add-meal-empty"
          >
            No recipes of type “{slot}” available. Create one in the Recipes tab first.
          </div>
        ) : null}
        {recipes.length > 0 ? (
          <ul className="flex max-h-[60vh] flex-col gap-2 overflow-y-auto">
            {recipes.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  data-testid={`add-meal-pick-${r.id}`}
                  onClick={() => void pick(r.id)}
                  disabled={pickingId !== null}
                  className="flex w-full items-center justify-between rounded border px-3 py-2 text-left text-sm"
                  style={{
                    background: 'var(--bg-surface)',
                    borderColor: 'var(--border)',
                    color: 'var(--text-main)',
                  }}
                >
                  <span className="truncate">{r.title}</span>
                  <span
                    className="ml-2 shrink-0 text-xs"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    {Math.round(r.per_portion.kcal)} kcal · P {r.per_portion.protein_g.toFixed(1)}g
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Dialog>
  );
}
